import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  CurrencyInr, SignOut, ArrowsClockwise, DownloadSimple, ChartBar, Receipt,
  ArrowsLeftRight, BellRinging, UploadSimple, Sparkle, Storefront,
  CaretRight, CaretDown, Wallet, ClipboardText, ListChecks, ShoppingCart,
  CalendarBlank, FilePdf, List as ListIcon, X as XIcon, Crosshair, Coins,
} from "@phosphor-icons/react";
import KpiStrip from "@/components/KpiStrip";
import ApprovalsTab from "@/components/ApprovalsTab";
import ReconciliationTab from "@/components/ReconciliationTab";
import AlertsTab from "@/components/AlertsTab";
import UploadsTab from "@/components/UploadsTab";
import CashHealthTab from "@/components/CashHealthTab";
import ClosingTab from "@/components/ClosingTab";
import OutstandingTab from "@/components/OutstandingTab";
import POUploadTab from "@/components/POUploadTab";
import NewPOUploadTab from "@/components/NewPOUploadTab";
import UpcomingExpensesTab from "@/components/UpcomingExpensesTab";
import PaymentsHistoryTab from "@/components/PaymentsHistoryTab";
import PredictionTab from "@/components/PredictionTab";
import VendorLogTab from "@/components/VendorLogTab";
import CashCollectionTab from "@/components/CashCollectionTab";
import ProfitLossTab from "@/components/ProfitLossTab";
import SettingsTab from "@/components/SettingsTab";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";

// Tree-shaped navigation. Top-level entries can have `children` (nested leaves).
const NAV_TREE = [
  {
    id: "finance", label: "Finance", icon: Wallet, children: [
      { id: "cash",       label: "Cash Health",    icon: ChartBar },
      { id: "approvals",  label: "Approvals",      icon: Receipt },
      { id: "recon",      label: "Reconciliation", icon: ArrowsLeftRight },
      { id: "prediction", label: "Prediction",     icon: Crosshair },
      { id: "vendor_log", label: "Vendor Log",     icon: Storefront },
      { id: "cash_collection", label: "Cash Collection", icon: Coins },
      { id: "pnl", label: "Profit & Loss", icon: ChartBar },
    ],
  },
  {
    id: "ops", label: "Operations", icon: ClipboardText, children: [
      { id: "closing",    label: "Daily Closing",  icon: Sparkle },
      { id: "alerts",     label: "Alerts & Tasks", icon: BellRinging },
    ],
  },
  {
    id: "data", label: "Data", icon: ListChecks, children: [
      { id: "uploads",      label: "Uploads",            icon: UploadSimple },
      { id: "outstanding",  label: "Outstanding",        icon: Wallet },
      { id: "payments_history", label: "Payments History", icon: ArrowsLeftRight },
      { id: "po_upload",    label: "GRN Upload",         icon: ShoppingCart },
      { id: "new_po",       label: "New PO upload for approval", icon: FilePdf },
      { id: "upcoming",     label: "Upcoming Expenses",  icon: CalendarBlank },
    ],
  },
  {
    id: "admin", label: "Admin", icon: ListChecks, children: [
      { id: "settings",     label: "Settings",           icon: Sparkle },
    ],
  },
];
const ALL_LEAF_IDS = NAV_TREE.flatMap((g) => g.children.map((c) => c.id));

// UI leaf-id ⇄ backend PERMISSION_TABS id mapping. Any leaf whose UI id
// differs from the backend tab id MUST appear here or its visibility check
// will silently pass (perms[id] === undefined → truthy default in the old
// code, which is exactly why Cash Health was leaking to Purchase / Viewer
// users). Keep this list next to the NAV_TREE so future additions stay in
// sync.
const NAV_ID_TO_PERM = {
  cash:  "cash_health",
  recon: "reconciliation",
};

// Returns true iff `user` may view the tab with this leaf-id.
// IMPORTANT: when `user` is null (initial /auth/me still loading) we return
// TRUE so the UI doesn't flash a "restricted" page to Owners/Admins during
// the first-paint window. The backend endpoints are RBAC-guarded, so a
// user who truly doesn't have access still sees no data — they just don't
// see a scary lock screen while the auth call is in flight.
function _canViewTab(user, leafId) {
  if (!user) return true;
  const role = (user.role || "").toLowerCase();
  if (role === "owner" || role === "admin") return true;
  const permId = NAV_ID_TO_PERM[leafId] || leafId;
  const perms = user.permissions || {};
  const p = perms[permId];
  return !!(p && p.view);
}

export default function Dashboard({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser || null);
  const [stores, setStores] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [tab, setTab] = useState("cash");

  // If the currently selected tab isn't allowed for this user, auto-pivot
  // to their first allowed tab. Prevents a Purchase user from ever seeing
  // the Cash Health placeholder — they land on Approvals (or whatever
  // their first allowed leaf is) instead.
  useEffect(() => {
    if (!user) return;
    if (_canViewTab(user, tab)) return;
    const firstAllowed = ALL_LEAF_IDS.find((id) => _canViewTab(user, id));
    if (firstAllowed && firstAllowed !== tab) setTab(firstAllowed);
  }, [user, tab]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openGroups, setOpenGroups] = useState(() => new Set(NAV_TREE.map((g) => g.id)));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const loadKpi = useCallback(async () => {
    try {
      const res = await api.get(`/kpi/cash-health`);
      setKpi(res.data);
    } catch {}
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {}
  }, []);

  const loadStores = useCallback(async () => {
    try {
      const res = await api.get(`/stores`);
      setStores(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) refreshUser();
    loadStores();
  }, [loadStores, user, refreshUser]);

  // Keep permissions fresh so a role edit in Settings takes effect without a
  // full logout / hard reload. Three triggers:
  //   1. Every 45 s while the tab is open (bounded background poll).
  //   2. When the tab regains focus (user comes back to the window).
  //   3. When the manual Refresh button is clicked (handleReset path).
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(refreshUser, 45000);
    const onFocus = () => { if (!document.hidden) refreshUser(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refreshUser]);

  useEffect(() => { loadKpi(); }, [loadKpi, refreshKey]);

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/login", { replace: true });
  };

  const [downloading, setDownloading] = useState(false);
  const downloadAll = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/export/reconik.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reconik-data-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Reconik data downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const refresh = () => { setRefreshKey((k) => k + 1); refreshUser(); };

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="dashboard-page">
      {/* Sticky dark command-bar header */}
      <header className="sticky top-0 z-50 bg-slate-900 text-white border-b border-slate-800">
        <div className="flex items-center gap-3 sm:gap-6 px-3 sm:px-6 lg:px-8 h-14">
          {/* Hamburger: only below lg, opens nav drawer */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="lg:hidden h-9 w-9 grid place-items-center rounded-sm text-slate-200 hover:bg-slate-800 hover:text-cyan-200"
                aria-label="Open navigation"
                data-testid="mobile-nav-trigger"
              >
                <ListIcon size={20} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 max-w-[85vw] bg-slate-950 border-slate-800 p-0 text-slate-100 flex flex-col"
              data-testid="mobile-nav-drawer"
            >
              <div className="flex items-center justify-between px-4 h-14 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 grid place-items-center rounded-sm bg-gradient-to-br from-red-500 to-red-700">
                    <CurrencyInr size={15} weight="bold" className="text-white" />
                  </div>
                  <span className="font-heading font-black text-sm brand-gradient">RECONIK</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-4">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 px-2 mb-3">
                  Workspace
                </div>
                <NavTree
                  user={user}
                  tab={tab}
                  openGroups={openGroups}
                  toggleGroup={toggleGroup}
                  onPick={(id) => { setTab(id); setMobileNavOpen(false); }}
                  testIdPrefix="mobile-"
                />
                <div className="mt-6 pt-4 border-t border-slate-800/60 mx-2 text-[10px] text-slate-500">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Storefront size={11} weight="duotone" /> <span className="font-bold uppercase tracking-wider">Outlets</span>
                  </div>
                  <div>{stores.length} active</div>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-3">
            <div className="h-8 w-8 grid place-items-center rounded-sm bg-gradient-to-br from-red-500 to-red-700 shadow-[0_0_18px_rgba(239,68,68,0.55)]">
              <CurrencyInr size={18} weight="bold" className="text-white" />
            </div>
            <div className="font-heading font-black text-base tracking-tight brand-gradient">
              RECONIK
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={refresh} className="h-8 rounded-sm text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="refresh-btn">
              <ArrowsClockwise size={14} weight="bold" />
              <span className="hidden sm:inline ml-1.5 text-xs">Refresh</span>
            </Button>

            {["owner", "admin"].includes((user?.role || "").toLowerCase()) && (
              <Button variant="outline" size="sm" onClick={downloadAll} disabled={downloading}
                className="h-8 rounded-sm bg-transparent border-emerald-800/60 text-emerald-300 hover:bg-emerald-950/50 hover:text-emerald-200"
                data-testid="download-reconik-btn">
                <DownloadSimple size={14} weight="bold" />
                <span className="hidden sm:inline ml-1.5 text-xs">{downloading ? "Preparing…" : "Download Reconik"}</span>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white grid place-items-center text-xs font-bold ml-2 ring-2 ring-slate-900"
                  data-testid="profile-menu-btn"
                >
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs font-semibold">{user?.name || "User"}</div>
                  <div className="text-[11px] text-zinc-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <SignOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Body: sidebar + main content */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar tree nav */}
        <aside
          className="hidden lg:flex lg:flex-col w-56 xl:w-64 shrink-0 border-r border-slate-200 bg-slate-900/40 px-3 py-5 sticky top-14 self-start h-[calc(100vh-3.5rem)] overflow-y-auto"
          data-testid="sidebar-nav"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 px-2 mb-3">
            Workspace
          </div>
          <NavTree
            user={user}
            tab={tab}
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            onPick={(id) => setTab(id)}
          />

          <div className="mt-auto pt-4 border-t border-slate-800/60 mx-2 text-[10px] text-slate-500">
            <div className="flex items-center gap-1.5 mb-1">
              <Storefront size={11} weight="duotone" /> <span className="font-bold uppercase tracking-wider">Outlets</span>
            </div>
            <div>{stores.length} active</div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 px-4 lg:px-6 xl:px-8 pt-6 pb-12">
          {/* Bank Balance / Inflow / Outflow / Net Position strip carries
              cash-health data — hide from any role that doesn't have
              cash_health.view. Backend endpoint /api/kpi/cash-health also
              returns 403 for these users so `kpi` will be null anyway, but
              hiding the strip removes the empty placeholder too. */}
          {_canViewTab(user, "cash") && (
            <KpiStrip kpi={kpi} refreshKey={refreshKey} />
          )}

          <div className="mt-6">
            {tab === "cash"      && _canViewTab(user, "cash") && (
              <CashHealthTab kpi={kpi} refreshKey={refreshKey} onRefresh={refresh} />
            )}
            {tab === "cash" && user && !_canViewTab(user, "cash") && (
              <ForbiddenPane tab="Cash Health" />
            )}
            {tab === "approvals" && <ApprovalsTab store={"all"} refreshKey={refreshKey} />}
            {tab === "recon"     && <ReconciliationTab refreshKey={refreshKey} />}
            {tab === "closing"   && <ClosingTab refreshKey={refreshKey} />}
            {tab === "alerts"    && <AlertsTab refreshKey={refreshKey} />}
            {tab === "uploads"     && <UploadsTab onUploaded={refresh} />}
            {tab === "outstanding" && <OutstandingTab refreshKey={refreshKey} onPaymentChange={loadKpi} />}
            {tab === "po_upload"   && <POUploadTab refreshKey={refreshKey} />}
            {tab === "new_po"      && <NewPOUploadTab refreshKey={refreshKey} onChange={refresh} />}
            {tab === "payments_history" && <PaymentsHistoryTab refreshKey={refreshKey} />}
            {tab === "prediction"  && <PredictionTab refreshKey={refreshKey} />}
            {tab === "vendor_log"  && <VendorLogTab refreshKey={refreshKey} />}
            {tab === "cash_collection" && <CashCollectionTab refreshKey={refreshKey} />}
            {tab === "pnl" && <ProfitLossTab refreshKey={refreshKey} />}
            {tab === "upcoming"    && <UpcomingExpensesTab refreshKey={refreshKey} onChange={loadKpi} />}
            {tab === "settings"    && <SettingsTab refreshKey={refreshKey} currentUser={user} onPermissionsChanged={refreshUser} />}
          </div>
        </main>
      </div>
    </div>
  );
}


// Shared nav-tree renderer used by both the sticky lg+ sidebar and the
// mobile slide-in Sheet. `testIdPrefix` lets mobile copies have unique
// `data-testid`s so playwright can target them independently.
function NavTree({ user, tab, openGroups, toggleGroup, onPick, testIdPrefix = "" }) {
  return (
    <nav className="space-y-1" data-testid={`${testIdPrefix}nav-tree`}>
      {NAV_TREE.map((g) => {
        const visibleChildren = g.children.filter((c) => _canViewTab(user, c.id));
        if (visibleChildren.length === 0) return null;
        const Icon = g.icon;
        const open = openGroups.has(g.id);
        const containsActive = visibleChildren.some((c) => c.id === tab);
        return (
          <div key={g.id}>
            <button
              onClick={() => toggleGroup(g.id)}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider ${
                containsActive ? "text-cyan-200" : "text-slate-300 hover:text-cyan-200"
              }`}
              data-testid={`${testIdPrefix}nav-group-${g.id}`}
            >
              {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
              <Icon size={13} weight="duotone" />
              <span>{g.label}</span>
            </button>
            {open && (
              <div className="ml-3 pl-3 border-l border-slate-700/60 mt-0.5 mb-2 space-y-0.5">
                {visibleChildren.map((c) => {
                  const CIcon = c.icon;
                  const active = tab === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => onPick(c.id)}
                      data-testid={`${testIdPrefix}nav-${c.id === "recon" ? "reconciliation" : c.id}`}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-sm text-left transition-colors ${
                        active
                          ? "bg-cyan-600/20 text-cyan-100 border-l-2 border-cyan-400 -ml-[1px] pl-[10px] font-semibold shadow-[0_0_18px_rgba(34,211,238,0.18)_inset]"
                          : "text-slate-300 hover:bg-slate-800/60 hover:text-cyan-100"
                      }`}
                    >
                      <CIcon size={14} weight={active ? "fill" : "duotone"} />
                      <span>{c.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}


// Rendered when a user visits a tab they don't have `view` permission on.
// Kept minimal — the sidebar already hides the entry so this is only ever
// seen if the user pastes a stale URL / refreshes on a tab that was
// visible pre-role-change.
function ForbiddenPane({ tab }) {
  return (
    <div
      className="mt-4 border border-rose-800/50 bg-rose-950/30 rounded-md p-8 text-center"
      data-testid="tab-forbidden"
    >
      <div className="text-4xl mb-3">🔒</div>
      <div className="font-heading text-xl font-bold text-rose-100">
        {tab} is restricted
      </div>
      <div className="text-sm text-rose-200/80 mt-2 max-w-md mx-auto">
        Your role doesn&apos;t have permission to view this section. If you need access, ask an Owner or Admin to grant you the <span className="font-mono text-rose-100">view</span> permission on <span className="font-mono text-rose-100">{tab}</span>.
      </div>
    </div>
  );
}
